import type {
  PortableAttachment,
  PortableBoardSnapshotV1,
  SnapshotAttachmentOutcome,
} from "@board-preserve/contracts";

export const MAX_LOCAL_ARCHIVE_BYTES = 1_000_000_000;

export interface CapturedAttachment {
  contents: null;
  outcome: SnapshotAttachmentOutcome;
}

export interface LocalAttachmentProgress {
  completed: number;
  linked: number;
  skipped: number;
  total: number;
}

export interface CaptureLocalAttachmentsOptions {
  onProgress?: (progress: LocalAttachmentProgress) => void;
  signal?: AbortSignal;
}

const classificationWarningMessages: Readonly<Record<string, string>> = {
  ATTACHMENT_SCHEME_UNSUPPORTED:
    "The attachment uses a source scheme that Local Export cannot safely link.",
  ATTACHMENT_URL_CREDENTIALS:
    "The attachment URL contains embedded credentials and was not included as a link.",
  ATTACHMENT_URL_INVALID:
    "The attachment URL is invalid and could not be included as a link.",
  ATTACHMENT_URL_MISSING:
    "The attachment does not include a source URL and could not be included as a link.",
};

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new DOMException("Local export cancelled.", "AbortError");
  }
}

function localAttachmentOutcome(
  attachment: PortableAttachment,
): SnapshotAttachmentOutcome {
  if (attachment.disposition !== "skip") {
    return {
      archivePath: null,
      attachmentId: attachment.id,
      byteSize: null,
      cardId: attachment.cardId,
      sha256: null,
      sourceType: attachment.sourceType,
      status: "link_only",
      warningCode: null,
      warningMessage: null,
    };
  }

  const warningCode =
    attachment.classificationWarningCode ?? "ATTACHMENT_URL_INVALID";
  return {
    archivePath: null,
    attachmentId: attachment.id,
    byteSize: null,
    cardId: attachment.cardId,
    sha256: null,
    sourceType: attachment.sourceType,
    status: "skipped",
    warningCode,
    warningMessage:
      classificationWarningMessages[warningCode] ??
      "The attachment could not be included as a safe link in Local Export.",
  };
}

/**
 * Records Local Export attachment references without fetching their contents.
 * Binary capture is deliberately reserved for the managed Cloud Preserve path.
 */
export async function captureLocalAttachments(
  snapshot: PortableBoardSnapshotV1,
  options: CaptureLocalAttachmentsOptions = {},
): Promise<readonly CapturedAttachment[]> {
  await Promise.resolve();
  let linked = 0;
  let skipped = 0;
  const results: CapturedAttachment[] = [];

  for (const [index, attachment] of snapshot.attachments.entries()) {
    throwIfAborted(options.signal);

    const outcome = localAttachmentOutcome(attachment);
    if (outcome.status === "link_only") {
      linked += 1;
    } else {
      skipped += 1;
    }
    results.push({ contents: null, outcome });

    options.onProgress?.({
      completed: index + 1,
      linked,
      skipped,
      total: snapshot.attachments.length,
    });
  }

  return results;
}
